import forEach from 'lodash/forEach';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {compose} from 'redux';
import {connect} from 'react-redux';
import uuidV4 from 'uuid/v4';
import moment from 'moment';
import IconSearch from '@spothero/icons/search';
import IconTimes from '@spothero/icons/times';
import Button from '@spothero/ui/button';
import DatePicker from '@spothero/ui/date-picker';
import TextButton from '@spothero/ui/text-button';
import TextInput from '@spothero/ui/text-input';
import SHLoader from 'common/loader/SHLoader';
import StorageUtils from '@spothero/utils/storage';
import {DESTINATION_DATA} from 'destination/destination-prop-types';
import withConfig from 'config/withConfig';
import {destinationLoadEvents} from 'destination/destination-actions';
import {push} from 'router/router-actions';
import DateUtils from 'utils/date';
import SegmentUtils from 'utils/segment';
import EventListItem from './EventListItem';

class EventList extends Component {
    static propTypes = {
        isMobile: PropTypes.bool.isRequired,
        displayDateFormat: PropTypes.string.isRequired,
        searchString: PropTypes.string,
        city: PropTypes.object,
        destination: PropTypes.shape(DESTINATION_DATA).isRequired,
        loadEvents: PropTypes.func.isRequired,
        showEventList: PropTypes.bool,
        onToggleEventList: PropTypes.func.isRequired,
        pushTo: PropTypes.func.isRequired,
        isPolishTimepickersEnabled: PropTypes.bool,
    };
    state = {
        isLoading: isUndefined(this.props.destination.events),
        date: '',
        name: '',
        key: uuidV4(),
    };

    componentDidMount() {
        const {
            searchString,
            destination: {title},
        } = this.props;
        const userLatLng = StorageUtils.get('sh-user-location', 'session');

        SegmentUtils.track({
            event: 'Event List Display',
            properties: {
                /* eslint-disable camelcase */
                ...(userLatLng && {user_location: userLatLng}),
                ...(searchString && {search_query: searchString}),
                destination: title,
                /* eslint-enable camelcase */
            },
        });

        this._loadData();
    }

    _onDateChange = selectedDate => {
        this.setState({
            date: selectedDate,
        });
    };

    _onInputChange = evt => {
        this.setState({
            name: evt.target.value,
        });
    };

    _onClearClick = evt => {
        this.setState({
            date: '',
            name: '',
            key: uuidV4(),
        });
    };

    _onEventSelected = event => {
        this.props.pushTo(`/${event.search_url}`);
    };

    _loadData = () => {
        // if the server loaded events for this destination on initial load then don't load them again
        if (!this.state.isLoading) {
            return;
        }

        this._asyncLoadData();
    };

    async _asyncLoadData() {
        const {
            destination: {id},
            loadEvents,
        } = this.props;

        try {
            await loadEvents({
                destinationId: id,
            });
        } catch (error) {
            console.log('EventList._loadData() error:', error); // eslint-disable-line no-console
        }

        this.setState({
            isLoading: false,
        });
    }

    _getFilteredEvents() {
        const {displayDateFormat, destination} = this.props;
        const {date, name} = this.state;
        const events = [];
        const hasDateFilter = !isEmpty(date);
        const hasNameFilter = !isEmpty(name);
        let formattedDate = null;

        forEach(destination.events, event => {
            formattedDate = moment(event.starts).format(displayDateFormat);

            if (hasNameFilter && hasDateFilter) {
                if (
                    includes(event.title.toUpperCase(), name.toUpperCase()) &&
                    includes(formattedDate, date)
                ) {
                    events.push(event);
                }
            } else if (hasNameFilter) {
                if (includes(event.title.toUpperCase(), name.toUpperCase())) {
                    events.push(event);
                }
            } else {
                if (includes(formattedDate, date)) {
                    events.push(event);
                }
            }
        });

        return events;
    }

    render() {
        const {
            isMobile,
            destination,
            city,
            showEventList,
            onToggleEventList,
            isPolishTimepickersEnabled,
        } = this.props;
        const {title} = destination;
        const {key, isLoading} = this.state;
        const classes = classNames('EventList', {
            'EventList-open': showEventList,
        });
        const events = this._getFilteredEvents();

        if (!destination.events || destination.events.length === 0) {
            return null;
        }

        return (
            <div className={classes}>
                <Button className="EventList-close" onClick={onToggleEventList}>
                    <IconTimes />
                </Button>
                {isLoading ? (
                    <div className="Loader-container">
                        <SHLoader
                            additionalMarkup={<p>Loading {title} events...</p>}
                        />
                    </div>
                ) : (
                    <Fragment>
                        <div className="EventList-static">
                            <h2 className="heading-md">
                                {isPolishTimepickersEnabled ? (
                                    'Find Parking for Events'
                                ) : (
                                    <>
                                        Going to an event
                                        {isMobile ? ` near ${title}` : ''}?
                                    </>
                                )}
                            </h2>
                            <p>
                                {isPolishTimepickersEnabled
                                    ? 'Going to an event? Select your event for accurate pricing and availability.'
                                    : 'Select your event to see accurate parking availability and price.'}
                            </p>
                            <div key={key} className="EventList-filters">
                                <DatePicker
                                    id="EventList-event-date"
                                    placeholder="Select Event Date"
                                    iconPosition="left"
                                    displayFormat={
                                        DateUtils.getTransientDateDisplayFormat
                                    }
                                    onChange={this._onDateChange}
                                />
                                <TextInput
                                    placeholder="Search Event Name"
                                    icon={<IconSearch />}
                                    onChange={this._onInputChange}
                                />
                            </div>
                        </div>
                        {events.length > 0 ? (
                            <div className="EventList-events">
                                {events.map(event => {
                                    return (
                                        <EventListItem
                                            key={event.id}
                                            event={event}
                                            destination={destination}
                                            city={city}
                                            onClick={this._onEventSelected}
                                            isMobile={false}
                                            isPolishTimepickersEnabled={
                                                isPolishTimepickersEnabled
                                            }
                                        />
                                    );
                                })}
                            </div>
                        ) : (
                            <p className="EventList-no-results">
                                No events match this search
                                <TextButton onClick={this._onClearClick}>
                                    Clear Filters
                                </TextButton>
                            </p>
                        )}
                    </Fragment>
                )}
            </div>
        );
    }
}

const mapStateToProps = state => {
    const {
        city: {data: cityData},
        destination: {data: destinationData},
        searchRequest: {search_string: searchString},
    } = state;

    return {
        city: cityData,
        destination: destinationData,
        searchString,
    };
};

const mapDispatchToProps = {
    loadEvents: destinationLoadEvents,
    pushTo: push,
};

const enhance = compose(
    withConfig(['displayDateFormat', 'isMobile']),
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
);

export default enhance(EventList);
